import React, { useContext, useEffect, useState } from "react";
import Axios from "axios";
import moment from "moment";
import "moment-timezone";
import { toast } from "react-toastify";
import { Typography } from "@material-ui/core";
import styled from "styled-components";

import AppContext from "../../../context/store";
import AuthContainer from "../../../components/AuthContainer";
import { Column, Grid } from "../../../theme/Grid";
import Table from "../../../theme/Table";
import AlertDialog from "../../../theme/CustomMUI/AlertDialog";
import LoaderEN from "../../../assets/images/circular.gif";
import LoaderSM from "../../../assets/images/loaderSM.gif";
import Loader from "../../../assets/images/loader.gif";

const PLATFORM_LABELS = {
  sms: "SMS",
  wp: "WP",
  both: "SMS + WP",
};

const normalizePlatform = (raw) => {
  const platform = (raw ?? "").toString().trim().toLowerCase();

  if (platform === "both" || platform === "sms+wp" || platform === "all") {
    return "both";
  }
  if (platform === "wp" || platform === "whatsapp") return "wp";

  return "sms";
};

const ScheduledMessages = () => {
  const context = useContext(AppContext);

  const urlPage = parseInt(
    new URLSearchParams(window.location.search).get("page")
  );

  const [scheduledMessages, setScheduledMessages] = useState({
    loaded: false,
    reports: [],
    pagination: {
      page: urlPage ? urlPage : 1,
      total_page: null,
      onChange: (page) =>
        setScheduledMessages((prev) => ({
          ...prev,
          pagination: { ...prev.pagination, page },
        })),
    },
  });

  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState({
    content: "",
    date: "",
    phone: "",
    platform: "sms",
    platform_label: "",
    numbers: {},
  });

  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    message_id: null,
  });
  const [cancelLoading, setCancelLoading] = useState(false);

  const numberEntries = (numbers) =>
    !numbers || typeof numbers !== "object" || Array.isArray(numbers)
      ? []
      : Object.entries(numbers);

  const formatRecipient = ([phone, name]) =>
    name ? `${name} (${phone})` : phone;

  const formatNumbers = (numbers, fallbackPhone = "") => {
    const entries = numberEntries(numbers);
    if (entries.length === 0) return fallbackPhone || "";

    return entries.map(formatRecipient).join(", ");
  };

  const mapScheduledMessage = (item) => {
    const sendDate = item?.send_date || item?.date || item?.send_time;
    const platform = normalizePlatform(item?.send_platform || item?.platform);
    const messageId = item?.ID ?? item?.id ?? item?.message_id;
    const entries = numberEntries(item?.numbers);

    return {
      id: messageId,
      message_id: messageId,
      date: sendDate
        ? moment(sendDate).tz("Europe/Istanbul").format("DD MMMM YYYY, HH:mm")
        : "",
      content: item?.message || item?.content || "",
      phone: formatNumbers(item?.numbers, item?.phone),
      phone_preview:
        entries.length > 0 ? formatRecipient(entries[0]) : item?.phone || "",
      remaining_recipients: entries.length > 1 ? entries.length - 1 : 0,
      send_platform: platform,
      platform_label: PLATFORM_LABELS[platform],
      numbers: item?.numbers || {},
    };
  };

  const getScheduledMessages = () => {
    setScheduledMessages((prev) => ({
      ...prev,
      loaded: false,
    }));

    Axios.get(`${context.api_endpoint}/company/message/scheduled`, {
      params: {
        page: scheduledMessages.pagination.page
          ? scheduledMessages.pagination.page
          : 1,
      },
    })
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const payload = response.data?.data;
          const records = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.records)
              ? payload.records
              : [];
          const hasServerPagination = payload && !Array.isArray(payload);

          setScheduledMessages((prev) => ({
            ...prev,
            reports: records.map(mapScheduledMessage),
            pagination: hasServerPagination
              ? {
                  ...prev.pagination,
                  page: payload?.page || prev.pagination.page || 1,
                  total_page: payload?.total_page || records.length || 0,
                }
              : {
                  ...prev.pagination,
                  page: 1,
                  total_page: 0,
                },
          }));
        }
      })
      .catch((e) => {
        setScheduledMessages((prev) => ({
          ...prev,
          reports: [],
        }));
        if (e.response?.status === 404) {
          toast.error(e.response?.data?.message);
        } else {
          toast.warn(e.response?.data?.message || "Bir hata oluştu");
        }
      })
      .finally(() => {
        setScheduledMessages((prev) => ({
          ...prev,
          loaded: true,
        }));
      });
  };

  const closeCancelDialog = () => {
    setCancelDialog({
      open: false,
      message_id: null,
    });
  };

  const cancelScheduledMessage = () => {
    if (!cancelDialog.message_id) {
      toast.warning(context.t(`sms.resendMissingIdToast`));
      return;
    }

    setCancelLoading(true);
    Axios.post(`${context.api_endpoint}/company/message/scheduled/cancel`, {
      id: cancelDialog.message_id,
    })
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(
            response.data?.message ||
              response.data?.data ||
              context.t(`sms.cancelScheduledSuccessToast`)
          );
          closeCancelDialog();
          getScheduledMessages();
        }
      })
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 404) {
          toast.error(e.response?.data?.message);
        } else {
          toast.warning(
            e.response?.data?.message ||
              context.t(`sms.cancelScheduledErrorToast`)
          );
        }
      })
      .finally(() => {
        setCancelLoading(false);
      });
  };

  useEffect(() => {
    getScheduledMessages();
  }, [scheduledMessages.pagination.page]);

  return (
    <AuthContainer authorities={[1]} limited_permission="sms">
      <Grid>
        <Column className="xs-12 sm-12">
          {scheduledMessages.loaded === true ? (
            <Table
              refreshAction={() => getScheduledMessages()}
              headings={{
                date: {
                  label: context.t(`sms.headings.scheduledDate`),
                  style: { width: 220, whiteSpace: "nowrap" },
                  render: (row) => row.date || "-",
                },
                phone: {
                  label: context.t(`sms.headings.phone`),
                  style: { maxWidth: 240 },
                  render: (row) =>
                    row.phone_preview ? (
                      <RecipientPreview title={row.phone}>
                        <span className="preview">{row.phone_preview}</span>
                        {row.remaining_recipients > 0 && (
                          <span className="more">
                            {context.t(`sms.scheduledRemainingRecipients`, {
                              count: row.remaining_recipients,
                            })}
                          </span>
                        )}
                      </RecipientPreview>
                    ) : (
                      "-"
                    ),
                },
                platform_label: {
                  label: context.t(`sms.headings.platform`),
                  style: { width: 120 },
                  render: (row) => row.platform_label || "-",
                },
                content: {
                  label: context.t(`sms.headings.content`),
                  limited_line: 1,
                  style: { maxWidth: 400 },
                  render: (row) => row.content || "-",
                },
                _: {
                  label: context.t(`component.actionHeadingText`),
                },
              }}
              buttons={[
                {
                  icon: "sms",
                  title: context.t(`sms.sendButtonText`),
                  textColor: "green",
                  onClick: (row) => {
                    setDetail({
                      content: row.content,
                      date: row.date,
                      phone: row.phone,
                      platform: row.send_platform,
                      platform_label: row.platform_label,
                      numbers: row.numbers || {},
                    });
                    setShowDetail(true);
                  },
                },
                {
                  icon: "cancel",
                  title: context.t(`sms.cancelScheduledButtonTitle`),
                  textColor: "red",
                  onClick: (row) =>
                    setCancelDialog({
                      open: true,
                      message_id: row?.id ?? row?.ID ?? row?.message_id,
                    }),
                },
              ]}
              rows={scheduledMessages.reports}
              loaded={scheduledMessages.loaded}
              pagination={
                scheduledMessages.pagination.total_page > 0
                  ? scheduledMessages.pagination
                  : undefined
              }
            />
          ) : (
            <LoaderContainer>
              <img
                src={
                  process.env.APP_NAME === "salon"
                    ? Loader
                    : process.env.APP_NAME === "en"
                      ? LoaderEN
                      : LoaderSM
                }
                width="100"
                height="100"
                alt="loading"
              />
            </LoaderContainer>
          )}
        </Column>
      </Grid>

      <AlertDialog
        maxWidth="sm"
        fullWidth={true}
        open={showDetail}
        closeHandler={() => {
          setShowDetail(false);
          setDetail({
            content: "",
            date: "",
            phone: "",
            platform: "sms",
            platform_label: "",
            numbers: {},
          });
        }}
        title={context.t(`sms.scheduledDetailDialogTitle`)}
        buttons={[
          {
            title: context.t(`sms.buttonApprove`),
            icon: "close",
            backgroundColor: "primary-opacity",
            textColor: "primary",
          },
        ]}
      >
        <DetailGrid>
          <DetailRow>
            <DetailLabel>{context.t(`sms.headings.platform`)}</DetailLabel>
            <PlatformBadge $platform={detail.platform}>
              {detail.platform_label || "-"}
            </PlatformBadge>
          </DetailRow>

          <DetailRow>
            <DetailLabel>{context.t(`sms.headings.scheduledDate`)}</DetailLabel>
            <DetailValue>{detail.date || "-"}</DetailValue>
          </DetailRow>

          <DetailRow>
            <DetailLabel>
              {context.t(`sms.scheduledRecipientsLabel`)}
            </DetailLabel>
            <RecipientsList>
              {Object.entries(detail.numbers || {}).length > 0 ? (
                Object.entries(detail.numbers).map(([phone, name]) => (
                  <RecipientItem key={phone}>
                    <span className="name">{name || "-"}</span>
                    <span className="phone">{phone}</span>
                  </RecipientItem>
                ))
              ) : (
                <DetailValue>{detail.phone || "-"}</DetailValue>
              )}
            </RecipientsList>
          </DetailRow>

          <DetailRow>
            <DetailLabel>{context.t(`sms.headings.content`)}</DetailLabel>
            <MessageBox>{detail.content || "-"}</MessageBox>
          </DetailRow>
        </DetailGrid>
      </AlertDialog>

      <AlertDialog
        maxWidth="xs"
        fullWidth={true}
        open={cancelDialog.open}
        closeHandler={closeCancelDialog}
        title={context.t(`sms.cancelScheduledDialogTitle`)}
        buttons={[
          {
            title: context.t(`sms.cancelScheduledConfirmButton`),
            icon: "cancel",
            backgroundColor: "red",
            textColor: "white",
            disabled: cancelLoading,
            validationButton: true,
            disabledHandleClose: true,
            onClick: () => cancelScheduledMessage(),
          },
          {
            title: context.t(`sms.buttonApprove`),
            icon: "close",
            backgroundColor: "primary-opacity",
            textColor: "primary",
            onClick: () => closeCancelDialog(),
          },
        ]}
      >
        <Typography>
          {context.t(`sms.cancelScheduledDialogDescription`)}
        </Typography>
      </AlertDialog>
    </AuthContainer>
  );
};

export default ScheduledMessages;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const RecipientPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;

  .preview {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .more {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: #3f51b5;
    background-color: rgba(63, 81, 181, 0.12);
  }
`;

const DetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #666;
`;

const DetailValue = styled.span`
  font-size: 15px;
  color: #222;
`;

const PLATFORM_COLORS = {
  sms: { color: "#E67E22", background: "rgba(230, 126, 34, 0.12)" },
  wp: { color: "#128C7E", background: "rgba(18, 140, 126, 0.12)" },
  both: { color: "#3F51B5", background: "rgba(63, 81, 181, 0.12)" },
};

const PlatformBadge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) =>
    (PLATFORM_COLORS[props.$platform] || PLATFORM_COLORS.sms).color};
  background-color: ${(props) =>
    (PLATFORM_COLORS[props.$platform] || PLATFORM_COLORS.sms).background};
`;

const RecipientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecipientItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #f7f8fa;
  border: 1px solid #ececec;

  .name {
    font-size: 14px;
    font-weight: 500;
    color: #222;
  }

  .phone {
    font-size: 13px;
    color: #666;
    white-space: nowrap;
  }
`;

const MessageBox = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  background-color: #f7f8fa;
  border: 1px solid #e3e6ea;
  font-size: 15px;
  line-height: 1.6;
  color: #222;
  white-space: pre-wrap;
  word-break: break-word;
`;
